import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Faculty Certificate Generation
 * - View completed students
 * - Generate individual or batch certificates
 * - Choose certificate layout/template
 */
const FacultyCertificates = ({ facultyId }) => {
  const [completedStudents, setCompletedStudents] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [publishing, setPublishing] = useState({});
  const [selectedLayout, setSelectedLayout] = useState('');
  const [toast, setToast] = useState(null);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [filterCourse, setFilterCourse] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    if (!facultyId) return;
    try {
      setLoading(true);
      const [studentsRes, layoutsRes] = await Promise.all([
        axios.get(`/api/faculty/${facultyId}/completed-students`),
        axios.get('/api/certificate-layouts')
      ]);
      setCompletedStudents(studentsRes.data || []);
      const layoutData = layoutsRes.data || [];
      setLayouts(layoutData);
      const defaultLayout = layoutData.find(l => l.is_default);
      if (defaultLayout) setSelectedLayout(defaultLayout.id.toString());
      else if (layoutData.length > 0) setSelectedLayout(layoutData[0].id.toString());
    } catch (err) {
      console.error('Error loading certificate data:', err);
    } finally {
      setLoading(false);
    }
  }, [facultyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleGenerate = async (student) => {
    if (!selectedLayout) {
      showToast('Please select a certificate layout first', 'error');
      return;
    }
    const key = `${student.student_id}_${student.course_id}`;
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post('/api/faculty/generate-certificate', {
        student_id: student.student_id,
        course_id: student.course_id,
        layout_id: parseInt(selectedLayout),
        faculty_id: facultyId
      });
      showToast(`Certificate generated for ${student.student_name}`);
      loadData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate certificate';
      showToast(msg, 'error');
    } finally {
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handlePublish = async (student) => {
    const key = `${student.student_id}_${student.course_id}`;
    setPublishing(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post('/api/faculty/publish-certificate', {
        certificate_id: student.certificate_id
      });
      showToast(`Certificate published for ${student.student_name}. Student can now download it.`);
      loadData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to publish certificate';
      showToast(msg, 'error');
    } finally {
      setPublishing(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleBatchGenerate = async () => {
    if (!selectedLayout) {
      showToast('Please select a certificate layout first', 'error');
      return;
    }
    const eligible = filteredStudents.filter(s => !s.certificate_issued);
    if (eligible.length === 0) {
      showToast('No eligible students for batch generation', 'error');
      return;
    }
    if (!window.confirm(`Generate certificates for ${eligible.length} student(s)?`)) return;
    setBatchGenerating(true);
    try {
      const res = await axios.post('/api/faculty/batch-generate-certificates', {
        students: eligible.map(s => ({
          student_id: s.student_id,
          course_id: s.course_id
        })),
        layout_id: parseInt(selectedLayout),
        faculty_id: facultyId
      });
      showToast(`Generated ${res.data.generated || 0} certificates!`);
      loadData();
    } catch (err) {
      showToast('Batch generation failed', 'error');
    } finally {
      setBatchGenerating(false);
    }
  };

  const handleBatchPublish = async () => {
    const unpublished = filteredStudents.filter(s => s.certificate_issued && !s.certificate_published);
    if (unpublished.length === 0) {
      showToast('No certificates to publish', 'error');
      return;
    }
    if (!window.confirm(`Publish ${unpublished.length} certificate(s) to students?`)) return;
    setBatchGenerating(true);
    try {
      const res = await axios.post('/api/faculty/batch-publish-certificates', {
        certificate_ids: unpublished.map(s => s.certificate_id)
      });
      showToast(`Published ${res.data.published || 0} certificates!`);
      loadData();
    } catch (err) {
      showToast('Batch publish failed', 'error');
    } finally {
      setBatchGenerating(false);
    }
  };

  // Get unique courses for filter
  const courses = [...new Map(completedStudents.map(s => [s.course_id, { id: s.course_id, name: s.course_name }])).values()];

  // Filter students
  const filteredStudents = completedStudents.filter(s => {
    if (filterCourse && s.course_id !== filterCourse) return false;
    if (searchTerm && !s.student_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !s.student_id.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const eligibleCount = filteredStudents.filter(s => !s.certificate_issued).length;
  const unpublishedCount = filteredStudents.filter(s => s.certificate_issued && !s.certificate_published).length;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>Loading certificate data...</div>;
  }

  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1f2937' }}>🏆 Certificates</h2>
          <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '14px' }}>
            Generate certificates for students who have completed courses
          </p>
        </div>
      </div>

      {/* Controls Row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Layout selector */}
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '13px', color: '#374151', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Certificate Template</label>
          <select
            value={selectedLayout}
            onChange={(e) => setSelectedLayout(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', minWidth: '200px' }}
          >
            <option value="">Select template...</option>
            {layouts.map(l => (
              <option key={l.id} value={l.id}>{l.layout_name} {l.is_default ? '(Default)' : ''}</option>
            ))}
          </select>
        </div>

        {/* Course filter */}
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '13px', color: '#374151', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Filter by Course</label>
          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', minWidth: '200px' }}
          >
            <option value="">All Courses</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ fontSize: '13px', color: '#374151', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Search Student</label>
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
          />
        </div>

        {/* Batch generate */}
        {eligibleCount > 0 && (
          <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
            <button
              onClick={handleBatchGenerate}
              disabled={batchGenerating || !selectedLayout}
              style={{
                padding: '10px 20px', background: batchGenerating ? '#9ca3af' : '#7c3aed', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: batchGenerating ? 'wait' : 'pointer',
                fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              {batchGenerating ? '⏳ Processing...' : `🎓 Generate All (${eligibleCount})`}
            </button>
          </div>
        )}
        {unpublishedCount > 0 && (
          <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
            <button
              onClick={handleBatchPublish}
              disabled={batchGenerating}
              style={{
                padding: '10px 20px', background: batchGenerating ? '#9ca3af' : '#059669', color: '#fff',
                border: 'none', borderRadius: '8px', cursor: batchGenerating ? 'wait' : 'pointer',
                fontWeight: 600, whiteSpace: 'nowrap'
              }}
            >
              {batchGenerating ? '⏳ Processing...' : `📤 Publish All (${unpublishedCount})`}
            </button>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 150px', background: '#f0fdf4', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#059669' }}>{completedStudents.length}</div>
          <div style={{ fontSize: '13px', color: '#065f46' }}>Completed Students</div>
        </div>
        <div style={{ flex: '1 1 150px', background: '#eff6ff', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#2563eb' }}>
            {completedStudents.filter(s => s.certificate_issued && !s.certificate_published).length}
          </div>
          <div style={{ fontSize: '13px', color: '#1e40af' }}>Generated (Unpublished)</div>
        </div>
        <div style={{ flex: '1 1 150px', background: '#f0fdf4', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#059669' }}>
            {completedStudents.filter(s => s.certificate_published).length}
          </div>
          <div style={{ fontSize: '13px', color: '#065f46' }}>Published to Students</div>
        </div>
        <div style={{ flex: '1 1 150px', background: '#fefce8', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #fef08a' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#ca8a04' }}>
            {completedStudents.filter(s => !s.certificate_issued).length}
          </div>
          <div style={{ fontSize: '13px', color: '#854d0e' }}>Pending Generation</div>
        </div>
      </div>

      {/* Students Table */}
      {filteredStudents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', background: '#f9fafb', borderRadius: '12px', border: '2px dashed #d1d5db' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎓</div>
          <h3 style={{ color: '#374151' }}>No Completed Students</h3>
          <p style={{ color: '#6b7280' }}>
            {completedStudents.length === 0
              ? 'Students who complete all course weeks will appear here.'
              : 'No students match the current filters.'}
          </p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={thStyle}>Student</th>
                  <th style={thStyle}>Course</th>
                  <th style={thStyle}>Progress</th>
                  <th style={thStyle}>Grade</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student, index) => {
                  const key = `${student.student_id}_${student.course_id}`;
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #f3f4f6', background: index % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>{student.student_name}</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{student.student_id}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ color: '#374151' }}>{student.course_name}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: '1', height: '8px', background: '#e5e7eb', borderRadius: '4px', maxWidth: '100px' }}>
                            <div style={{ width: `${student.progress || 100}%`, height: '100%', background: '#059669', borderRadius: '4px' }} />
                          </div>
                          <span style={{ fontSize: '13px', color: '#6b7280' }}>{student.progress || 100}%</span>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{student.final_marks || 'N/A'}</span>
                      </td>
                      <td style={tdStyle}>
                        {student.certificate_published ? (
                          <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                            ✓ Published
                          </span>
                        ) : student.certificate_issued ? (
                          <span style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                            Generated
                          </span>
                        ) : (
                          <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                            Pending
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {student.certificate_published ? (
                          <span style={{ fontSize: '13px', color: '#059669', fontWeight: 500 }}>✓ Published</span>
                        ) : student.certificate_issued ? (
                          <button
                            onClick={() => handlePublish(student)}
                            disabled={publishing[key]}
                            style={{
                              padding: '6px 16px',
                              background: publishing[key] ? '#9ca3af' : '#059669',
                              color: '#fff', border: 'none', borderRadius: '6px',
                              cursor: publishing[key] ? 'wait' : 'pointer',
                              fontSize: '13px', fontWeight: 500
                            }}
                          >
                            {publishing[key] ? '⏳ Publishing...' : '📤 Publish'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleGenerate(student)}
                            disabled={generating[key] || !selectedLayout}
                            style={{
                              padding: '6px 16px',
                              background: generating[key] ? '#9ca3af' : '#2563eb',
                              color: '#fff', border: 'none', borderRadius: '6px',
                              cursor: generating[key] ? 'wait' : 'pointer',
                              fontSize: '13px', fontWeight: 500
                            }}
                          >
                            {generating[key] ? '⏳ Generating...' : '📜 Generate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Layouts info */}
      {layouts.length === 0 && (
        <div style={{ marginTop: '20px', padding: '16px', background: '#fef3c7', borderRadius: '10px', border: '1px solid #fde68a' }}>
          <strong>⚠️ No Certificate Templates Found</strong>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#92400e' }}>
            Ask your administrator to seed certificate templates from the admin panel before generating certificates.
          </p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', padding: '14px 24px',
          background: toast.type === 'error' ? '#ef4444' : '#059669',
          color: '#fff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 2000, fontWeight: 500
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' };
const tdStyle = { padding: '12px 16px', fontSize: '14px' };

export default FacultyCertificates;
